import React from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton,
  Box, Typography, Card, CardActionArea, Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ExploreIcon from "@mui/icons-material/Explore";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { useHelpStore } from "../store/helpStore.js";

// Sezioni → guida dedicata. La prima il cui `route` è prefisso del path corrente
// (con match più lungo = più specifico) viene mostrata come "guida di questa pagina".
interface Section { route: string; key: string; create: boolean }
const SECTIONS: Section[] = [
  { route: "/protected/partners", key: "partners", create: true },
  { route: "/protected/suppliers", key: "suppliers", create: true },
  { route: "/protected/services-acquired", key: "acqservices", create: true },
  { route: "/protected/services", key: "services", create: true },
  { route: "/protected/partner-services", key: "partnerServices", create: true },
  { route: "/protected/credits-generate", key: "creditsGen", create: false },
  { route: "/protected/credits", key: "credits", create: false },
  { route: "/protected/debits", key: "debits", create: true },
  { route: "/protected/receipts", key: "receipts", create: false },
  { route: "/protected/costs", key: "costs", create: true },
  { route: "/protected/summary", key: "summary", create: false },
  { route: "/protected/statistics", key: "statistics", create: false },
  { route: "/protected/whatsapp/compose", key: "waCompose", create: false },
  { route: "/protected/whatsapp/queues", key: "waQueues", create: false },
  { route: "/protected/whatsapp/templates", key: "waTemplates", create: true },
  { route: "/protected/whatsapp/messages", key: "waMessages", create: false },
  // Scheda ricevuta (path singolare, distinto da /receipts) e profilo utente.
  { route: "/protected/receipt/", key: "receiptDetail", create: false },
  { route: "/protected/partnersServices", key: "partnerServices", create: true },
  { route: "/protected/users", key: "user", create: false },
  { route: "/protected", key: "dashboard", create: false }
];

// Titolo/descrizione mostrati nella card del Centro guide (alcune sezioni non sono sotto help.sec.*).
function cardTitleKey(key: string): string {
  if (key === "receiptDetail") return "help.rd.intro.title";
  if (key === "user") return "help.user.intro.title";
  return `help.sec.${key}.title`;
}
function cardDescKey(key: string): string {
  if (key === "receiptDetail") return "help.rd.intro.desc";
  if (key === "user") return "help.user.intro.desc";
  return `help.sec.${key}.intro`;
}

function matchSection(pathname: string): Section | null {
  let best: Section | null = null;
  for (const s of SECTIONS) {
    if (pathname === s.route || pathname.startsWith(s.route)) {
      if (!best || s.route.length > best.route.length) best = s;
    }
  }
  return best;
}

type T = (key: string) => string;

function runSteps(rawSteps: DriveStep[], t: T) {
  // Tiene solo gli step senza elemento (popover centrale) o il cui elemento esiste a schermo.
  const steps = rawSteps.filter((s) => !s.element || document.querySelector(s.element as string));
  if (steps.length === 0) return;
  const d = driver({
    showProgress: true,
    allowClose: true,
    overlayOpacity: 0.65,
    stagePadding: 6,
    stageRadius: 8,
    smoothScroll: true,
    animate: true,
    nextBtnText: t("help.next"),
    prevBtnText: t("help.prev"),
    doneBtnText: t("help.done"),
    steps
  });
  d.drive();
}

// Statistiche: guida con ingresso in ogni sotto-tab e ritorno alla tab di partenza.
// Avvia un driver dedicato perché serve cambiare tab (click reale) e ripristinarla a fine guida.
function runStatisticsTour(t: T) {
  const tabEl = (k: string) => document.querySelector(`[data-tour="st-tab-${k}"]`) as HTMLElement | null;
  const TABS = ["soci", "servizi", "insoluti", "sommario"];
  const initial = TABS.find((k) => tabEl(k)?.getAttribute("aria-selected") === "true") ?? "soci";
  const click = (k: string) => tabEl(k)?.click();

  const tabStep = (k: string): DriveStep => ({
    element: '[data-tour="st-content"]',
    popover: { title: t(`help.st.${k}.title`), description: t(`help.st.${k}.desc`) },
    onHighlightStarted: () => click(k)
  });

  const steps: DriveStep[] = [
    { popover: { title: t("help.sec.statistics.title"), description: t("help.sec.statistics.intro") } },
    { element: '[data-tour="st-period"]', popover: { title: t("help.st.period.title"), description: t("help.st.period.desc") } },
    { element: '[data-tour="st-types"]', popover: { title: t("help.st.types.title"), description: t("help.st.types.desc") } },
    { element: '[data-tour="st-actions"]', popover: { title: t("help.st.actions.title"), description: t("help.st.actions.desc") } },
    { element: '[data-tour="st-kpi"]', popover: { title: t("help.st.kpi.title"), description: t("help.st.kpi.desc") } },
    tabStep("soci"),
    tabStep("servizi"),
    tabStep("insoluti"),
    tabStep("sommario")
  ].filter((s) => !s.element || document.querySelector(s.element as string));

  if (steps.length === 0) return;
  driver({
    showProgress: true,
    allowClose: true,
    overlayOpacity: 0.65,
    stagePadding: 6,
    stageRadius: 8,
    smoothScroll: true,
    animate: true,
    nextBtnText: t("help.next"),
    prevBtnText: t("help.prev"),
    doneBtnText: t("help.done"),
    onDestroyed: () => click(initial), // torna alla tab di partenza
    steps
  }).drive();
}

// Riepilogo: giro completo su Cruscotto e Dettaglio, con ritorno alla tab di partenza.
function runSummaryTour(t: T) {
  const tabEl = (k: string) => document.querySelector(`[data-tour="sm-tab-${k}"]`) as HTMLElement | null;
  const TABS = ["dashboard", "detail"];
  const initial = TABS.find((k) => tabEl(k)?.getAttribute("aria-selected") === "true") ?? "dashboard";
  const click = (k: string) => tabEl(k)?.click();

  const steps: DriveStep[] = [
    { popover: { title: t("help.sec.summary.title"), description: t("help.sec.summary.intro") } },
    { element: '[data-tour="sm-period"]', popover: { title: t("help.sm.period.title"), description: t("help.sm.period.desc") } },
    { element: '[data-tour="sm-content"]', popover: { title: t("help.sm.dashboard.title"), description: t("help.sm.dashboard.desc") }, onHighlightStarted: () => click("dashboard") },
    { element: '[data-tour="sm-content"]', popover: { title: t("help.sm.detail.title"), description: t("help.sm.detail.desc") }, onHighlightStarted: () => click("detail") }
  ].filter((s) => !s.element || document.querySelector(s.element as string));

  if (steps.length === 0) return;
  driver({
    showProgress: true,
    allowClose: true,
    overlayOpacity: 0.65,
    stagePadding: 6,
    stageRadius: 8,
    smoothScroll: true,
    animate: true,
    nextBtnText: t("help.next"),
    prevBtnText: t("help.prev"),
    doneBtnText: t("help.done"),
    onDestroyed: () => click(initial),
    steps
  }).drive();
}

function overviewSteps(t: T): DriveStep[] {
  return [
    { element: '[data-tour="nav-toggle"]', popover: { title: t("help.navTitle"), description: t("help.navText") } },
    { element: '[data-tour="page-title"]', popover: { title: t("help.headerTitle"), description: t("help.headerText") } },
    { element: '[data-tour="page-refresh"]', popover: { title: t("help.refreshTitle"), description: t("help.refreshText") } },
    { element: '[data-tour="help"]', popover: { title: t("help.helpTitle"), description: t("help.helpText") } }
  ];
}

// Guida dedicata e passo-passo per "Preparazione mensili" (flusso particolare).
function creditsGenSteps(t: T): DriveStep[] {
  const s = (el: string, key: string): DriveStep => ({
    element: el,
    popover: { title: t(`help.cg.${key}.title`), description: t(`help.cg.${key}.desc`) }
  });
  return [
    { popover: { title: t("help.sec.creditsGen.title"), description: t("help.sec.creditsGen.intro") } },
    s('[data-tour="cg-period"]', "period"),
    s('[data-tour="cg-calc"]', "calc"),
    s('[data-tour="cg-service"]', "service"),
    s('[data-tour="cg-name"]', "customize"),
    s('[data-tour="cg-notify"]', "toggles"),
    s('[data-tour="cg-partners"]', "partners"),
    s('[data-tour="cg-add"]', "add"),
    s('[data-tour="cg-credits"]', "credits"),
    s('[data-tour="row-actions"]', "rowMenu"),
    { element: '[data-tour="page-refresh"]', popover: { title: t("help.refreshTitle"), description: t("help.refreshText") } }
  ];
}

// Guida dedicata per le Ricevute (filtri + azioni della scheda).
function receiptsSteps(t: T): DriveStep[] {
  const s = (el: string, key: string): DriveStep => ({
    element: el,
    popover: { title: t(`help.rc.${key}.title`), description: t(`help.rc.${key}.desc`) }
  });
  return [
    { popover: { title: t("help.sec.receipts.title"), description: t("help.sec.receipts.intro") } },
    s('[data-tour="rc-socio"]', "socio"),
    s('[data-tour="rc-year"]', "year"),
    s('[data-tour="rc-mode"]', "mode"),
    s('[data-tour="rc-active"]', "active"),
    s('[data-tour="rc-type"]', "type"),
    s('[data-tour="row-actions"]', "rowMenu"),
    { element: '[data-tour="page-refresh"]', popover: { title: t("help.refreshTitle"), description: t("help.refreshText") } }
  ];
}

// Guida dedicata per i Crediti (ricerca, periodo, totale, significato).
function creditsSteps(t: T): DriveStep[] {
  const s = (el: string, key: string): DriveStep => ({
    element: el,
    popover: { title: t(`help.cr.${key}.title`), description: t(`help.cr.${key}.desc`) }
  });
  return [
    { popover: { title: t("help.sec.credits.title"), description: t("help.sec.credits.intro") } },
    s('[data-tour="cr-search"]', "search"),
    s('[data-tour="cr-period"]', "period"),
    s('[data-tour="cr-total"]', "total"),
    { popover: { title: t("help.cr.meaning.title"), description: t("help.cr.meaning.desc") } },
    { element: '[data-tour="page-refresh"]', popover: { title: t("help.refreshTitle"), description: t("help.refreshText") } }
  ];
}

// ─── WhatsApp: guide dedicate per ogni pagina, con il flusso complessivo in apertura ───
function waFlowStep(t: T): DriveStep {
  return { popover: { title: t("help.wa.flow.title"), description: t("help.wa.flow.desc") } };
}
function waComposeSteps(t: T): DriveStep[] {
  const p = (el: string, key: string): DriveStep => ({ element: el, popover: { title: t(`help.wa.compose.${key}.title`), description: t(`help.wa.compose.${key}.desc`) } });
  return [
    { popover: { title: t("help.sec.waCompose.title"), description: t("help.sec.waCompose.intro") } },
    waFlowStep(t),
    p('[data-tour="wa-template"]', "template"),
    p('[data-tour="wa-preview"]', "preview"),
    p('[data-tour="wa-file"]', "file"),
    p('[data-tour="wa-filters"]', "filters"),
    p('[data-tour="wa-lists"]', "lists"),
    p('[data-tour="wa-enqueue"]', "enqueue")
  ];
}
function waQueuesSteps(t: T): DriveStep[] {
  return [
    { popover: { title: t("help.sec.waQueues.title"), description: t("help.sec.waQueues.intro") } },
    waFlowStep(t),
    { element: '[data-tour="wa-launch"]', popover: { title: t("help.wa.queues.launch.title"), description: t("help.wa.queues.launch.desc") } },
    { element: '[data-tour="row-actions"]', popover: { title: t("help.wa.queues.rowMenu.title"), description: t("help.wa.queues.rowMenu.desc") } },
    { element: '[data-tour="page-refresh"]', popover: { title: t("help.refreshTitle"), description: t("help.refreshText") } }
  ];
}
function waTemplatesSteps(t: T): DriveStep[] {
  return [
    { popover: { title: t("help.sec.waTemplates.title"), description: t("help.sec.waTemplates.intro") } },
    waFlowStep(t),
    { element: '[data-tour="page-action"]', popover: { title: t("help.wa.templates.create.title"), description: t("help.wa.templates.create.desc") } },
    { element: '[data-tour="row-actions"]', popover: { title: t("help.wa.templates.rowMenu.title"), description: t("help.wa.templates.rowMenu.desc") } },
    { element: '[data-tour="page-refresh"]', popover: { title: t("help.refreshTitle"), description: t("help.refreshText") } }
  ];
}
function waMessagesSteps(t: T): DriveStep[] {
  const p = (el: string, key: string): DriveStep => ({ element: el, popover: { title: t(`help.wa.messages.${key}.title`), description: t(`help.wa.messages.${key}.desc`) } });
  return [
    { popover: { title: t("help.sec.waMessages.title"), description: t("help.sec.waMessages.intro") } },
    waFlowStep(t),
    p('[data-tour="wa-msg-filters"]', "filters"),
    p('[data-tour="wa-msg-list"]', "list"),
    p('[data-tour="wa-msg-readmore"]', "readmore"),
    p('[data-tour="wa-msg-resend"]', "resend")
  ];
}

// Scheda ricevuta (dettaglio): riepilogo, pagamenti, competenze, azioni.
function receiptDetailSteps(t: T): DriveStep[] {
  const p = (el: string, key: string): DriveStep => ({ element: el, popover: { title: t(`help.rd.${key}.title`), description: t(`help.rd.${key}.desc`) } });
  return [
    { popover: { title: t("help.rd.intro.title"), description: t("help.rd.intro.desc") } },
    p('[data-tour="rd-summary"]', "summary"),
    p('[data-tour="rd-payments"]', "payments"),
    p('[data-tour="rd-pay-add"]', "payAdd"),
    p('[data-tour="rd-competences"]', "competences"),
    p('[data-tour="rd-actions"]', "actions")
  ];
}

function sectionSteps(section: Section, t: T): DriveStep[] {
  if (section.key === "creditsGen") return creditsGenSteps(t);
  if (section.key === "receipts") return receiptsSteps(t);
  if (section.key === "credits") return creditsSteps(t);
  if (section.key === "receiptDetail") return receiptDetailSteps(t);
  if (section.key === "user") return [{ popover: { title: t("help.user.intro.title"), description: t("help.user.intro.desc") } }];
  if (section.key === "waCompose") return waComposeSteps(t);
  if (section.key === "waQueues") return waQueuesSteps(t);
  if (section.key === "waTemplates") return waTemplatesSteps(t);
  if (section.key === "waMessages") return waMessagesSteps(t);
  const steps: DriveStep[] = [
    // 1) Introduzione alla sezione (popover centrale).
    { popover: { title: t(`help.sec.${section.key}.title`), description: t(`help.sec.${section.key}.intro`) } },
    // 2) "Come funziona": spiega tutte le casistiche della sezione.
    { popover: { title: t("help.detailsTitle"), description: t(`help.sec.${section.key}.details`) } }
  ];
  // Filtri dedicati dei Pagamenti (socio + periodo/anno/personalizzato).
  if (section.key === "debits") {
    steps.push({ element: '[data-tour="debits-socio"]', popover: { title: t("help.debitsSocioTitle"), description: t("help.debitsSocioText") } });
    steps.push({ element: '[data-tour="debits-filters"]', popover: { title: t("help.debitsFiltersTitle"), description: t("help.debitsFiltersText") } });
  }
  if (section.create) {
    steps.push({ element: '[data-tour="page-action"]', popover: { title: t("help.createTitle"), description: t("help.createText") } });
  }
  steps.push({ element: '[data-tour="row-actions"]', popover: { title: t("help.rowMenuTitle"), description: t("help.rowMenuText") } });
  steps.push({ element: '[data-tour="page-refresh"]', popover: { title: t("help.refreshTitle"), description: t("help.refreshText") } });
  return steps;
}

export function HelpCenter() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const open = useHelpStore((s) => s.open);
  const setOpen = useHelpStore((s) => s.setOpen);

  const section = matchSection(pathname);

  // Chiude il dialog e avvia la guida (attende la chiusura così l'overlay non copre gli elementi).
  const launchRun = (run: () => void) => {
    setOpen(false);
    setTimeout(run, 320);
  };
  const launchSection = () => {
    if (!section) return;
    if (section.key === "statistics") return launchRun(() => runStatisticsTour(t));
    if (section.key === "summary") return launchRun(() => runSummaryTour(t));
    launchRun(() => runSteps(sectionSteps(section, t), t));
  };

  const TourCard = ({ icon, title, desc, onClick }: { icon: React.ReactNode; title: string; desc: string; onClick: () => void }) => (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardActionArea onClick={onClick} sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ color: "primary.main", mt: 0.25 }}>{icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">{desc}</Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {t("help.title")}
          <IconButton onClick={() => setOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }} aria-label={t("help.done")}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("help.subtitle")}
          </Typography>

          {section && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary">{t("help.forThisPage")}</Typography>
              <TourCard
                icon={<PlayCircleOutlineIcon />}
                title={t(cardTitleKey(section.key))}
                desc={t(cardDescKey(section.key))}
                onClick={launchSection}
              />
            </Box>
          )}

          <Box>
            <Typography variant="overline" color="text.secondary">{t("help.general")}</Typography>
            <TourCard
              icon={<ExploreIcon />}
              title={t("help.overviewTitle")}
              desc={t("help.overviewDesc")}
              onClick={() => launchRun(() => runSteps(overviewSteps(t), t))}
            />
          </Box>
        </DialogContent>
    </Dialog>
  );
}
